import { Card, Col, Row, Space } from 'antd';
import { useFormikContext } from 'formik';
import { pick, keyBy } from 'lodash/fp';
import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { type PromoCodeFormValues } from '.';
import * as permissionKind from '../../../../../shared/permissions';
import { useGetModuleSpecsQuery } from '../../../../api/queries/getModuleSpecs';
import { DiscountType, ModuleRole, ModuleType, PromoTypeEnum } from '../../../../api/types';
import { useCompany } from '../../../../components/contexts/CompanyContextManager';
import DealerSelectField from '../../../../components/fields/DealerSelectField';
import InputField from '../../../../components/fields/InputField';
import InputGroup from '../../../../components/fields/InputGroup';
import InputNumberField from '../../../../components/fields/InputNumberField';
import ModuleSelectField from '../../../../components/fields/ModuleSelectField';
import RangePickerField from '../../../../components/fields/RangePickerField';
import SelectField from '../../../../components/fields/SelectField';
import SwitchField from '../../../../components/fields/SwitchField';
import TimePickerField from '../../../../components/fields/TimePickerField';
import { getOffsetOnly } from '../../../../utilities/date';
import hasPermissions from '../../../../utilities/hasPermissions';
import usePromoCodeOptions from '../../../../utilities/usePromoCodeOptions';
import useSystemSwitchData from '../../../../utilities/useSystemSwitchData';
import VehicleAssignmentsSection from '../../../shared/VehicleAssignmentsSection';

export type NewPromoCodeFormValues = PromoCodeFormValues & {
    moduleId: string;
};

export const defaultValues: NewPromoCodeFormValues = {
    promoCode: null,
    moduleId: null,
    period: null,
    promoType: {
        type: PromoTypeEnum.Discount,
        discountPromoType: {
            amount: null,
            deductFromFinancing: true,
            discountType: DiscountType.Percentage,
            description: null,
        },
        giftPromoType: {
            description: null,
        },
    },
    trackUsage: true,
    initialQuantity: null,
    dealerIds: [],
    variantModuleIds: [],
    moduleVariantsMapping: {},
    isActive: true,
    applicationModuleIds: [],
    endTime: null,
    startTime: null,
};

const colSpan = { lg: 8, md: 12, xs: 24 };

const CreatePromoCodeFrom = () => {
    const { t } = useTranslation(['promoCodeDetails']);
    const { initialValues, values, setFieldValue } = useFormikContext<NewPromoCodeFormValues>();
    const company = useCompany(true);

    const { yesNoSwitch } = useSystemSwitchData();

    const { data: moduleData } = useGetModuleSpecsQuery({
        fetchPolicy: 'cache-and-network',
        variables: {
            moduleId: values?.moduleId,
        },
        skip: !values?.moduleId,
    });

    const [applicationModulesData, applicationModulesMapping] = useMemo(() => {
        if (!values.moduleId) {
            // no promo code module selected
            return [];
        }

        const modulesData = company.modules.filter(module => {
            if (
                module.__typename === 'StandardApplicationModule' ||
                module.__typename === 'ConfiguratorModule' ||
                module.__typename === 'FinderApplicationPublicModule' ||
                module.__typename === 'FinderApplicationPrivateModule' ||
                module.__typename === 'MobilityModule'
            ) {
                return module.promoCodeModuleId === values.moduleId;
            }

            return false;
        });
        const modulesMapping = keyBy('id', modulesData);

        return [modulesData, modulesMapping];
    }, [company.modules, values.moduleId]);

    const [vehicleModulesData, vehicleModulesMapping] = useMemo(() => {
        const showFinderAssignment =
            company?.availableModules.hasFinderVehicleManagementModules &&
            hasPermissions(company?.permissions, [permissionKind.viewFinderVehicles]);
        const modulesData = company.modules.filter(
            module =>
                module.__typename.includes(ModuleType.SimpleVehicleManagement) ||
                (showFinderAssignment && module.__typename.includes(ModuleType.FinderVehicleManagement))
        );
        const modulesMapping = keyBy('id', modulesData);

        return [modulesData, modulesMapping];
    }, [company]);

    const hasApplicationModules = useMemo(() => {
        if (!values.applicationModuleIds.length) {
            return false;
        }

        return values.applicationModuleIds.some(moduleId =>
            [
                'StandardApplicationModule',
                'EventApplicationModule',
                'ConfiguratorModule',
                'FinderApplicationPublicModule',
                'FinderApplicationPrivateModule',
                'MobilityModule',
            ].includes(applicationModulesMapping[moduleId]?.__typename)
        );
    }, [applicationModulesMapping, values.applicationModuleIds]);

    useEffect(() => {
        if (company?.id && moduleData?.module?.companyId && moduleData.module.companyId !== company.id) {
            setFieldValue('moduleId', null);
        }
    }, [company, moduleData, setFieldValue]);

    useEffect(() => {
        setFieldValue('moduleVariantsMapping', pick(values.variantModuleIds, values.moduleVariantsMapping));

        // Currently ignore it, as it's become infinite loop if we add "values.moduleVariantsMapping"
        // and "setFieldValue" to the dependency array
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [values.variantModuleIds]);

    const { promoCodeTypeOptions, discountTypeOptions } = usePromoCodeOptions(moduleData?.module?.company?.currency);

    return (
        <Space direction="vertical" style={{ width: '100%' }}>
            <Card title={t('promoCodeDetails:cardTitles.promoCodeDetails')}>
                <Row gutter={14}>
                    <Col {...colSpan}>
                        <InputField
                            {...t('promoCodeDetails:fields.code', { returnObjects: true })}
                            name="promoCode"
                            required
                        />
                    </Col>
                    <Col {...colSpan}>
                        <RangePickerField
                            {...t('promoCodeDetails:fields.period', { returnObjects: true })}
                            name="period"
                            required
                        />
                    </Col>
                    <Col {...colSpan}>
                        <TimePickerField
                            {...t('promoCodeDetails:fields.startTime', {
                                returnObjects: true,
                            })}
                            name="startTime"
                            offset={getOffsetOnly(company?.timeZone)}
                            required
                        />
                    </Col>
                    <Col {...colSpan}>
                        <TimePickerField
                            {...t('promoCodeDetails:fields.endTime', {
                                returnObjects: true,
                            })}
                            name="endTime"
                            offset={getOffsetOnly(company?.timeZone)}
                            required
                        />
                    </Col>
                    <Col {...colSpan}>
                        <ModuleSelectField
                            {...t('promoCodeDetails:fields.module', { returnObjects: true })}
                            companyId={company?.id}
                            moduleRole={ModuleRole.PromoCode}
                            name="moduleId"
                            required
                        />
                    </Col>
                    {values?.moduleId && (
                        <Col {...colSpan}>
                            <ModuleSelectField
                                {...t('promoCodeDetails:fields.applicationModule', { returnObjects: true })}
                                companyId={company?.id}
                                mode="multiple"
                                modules={applicationModulesData}
                                name="applicationModuleIds"
                                required
                            />
                        </Col>
                    )}
                    {hasApplicationModules && (
                        <Col {...colSpan}>
                            <DealerSelectField
                                {...t('promoCodeDetails:fields.dealers', { returnObjects: true })}
                                companyId={company?.id}
                                forceSkipFetch={!company?.id}
                                mode="multiple"
                                name="dealerIds"
                            />
                        </Col>
                    )}
                    {hasApplicationModules && (
                        <Col {...colSpan}>
                            <ModuleSelectField
                                {...t('promoCodeDetails:fields.vehicleModule', { returnObjects: true })}
                                companyId={company?.id}
                                mode="multiple"
                                modules={vehicleModulesData}
                                name="variantModuleIds"
                            />
                        </Col>
                    )}
                    {hasApplicationModules && (
                        <Col {...colSpan}>
                            <SelectField
                                {...t('promoCodeDetails:fields.type', { returnObjects: true })}
                                name="promoType.type"
                                options={promoCodeTypeOptions}
                                required
                                showSearch
                            />
                        </Col>
                    )}
                    {hasApplicationModules && values.promoType.type === PromoTypeEnum.Discount && (
                        <>
                            <Col {...colSpan}>
                                <InputGroup
                                    {...t('promoCodeDetails:fields.discountAmount', { returnObjects: true })}
                                    required
                                >
                                    <SelectField
                                        disabled={!moduleData?.module?.companyId}
                                        itemProps={{ style: { maxWidth: '6rem' } }}
                                        name="promoType.discountPromoType.discountType"
                                        options={discountTypeOptions}
                                        showSearch
                                    />
                                    <InputNumberField
                                        disabled={!moduleData?.module?.companyId}
                                        name="promoType.discountPromoType.amount"
                                    />
                                </InputGroup>
                            </Col>
                            {hasApplicationModules && (
                                <Col {...colSpan}>
                                    <SwitchField
                                        {...t('promoCodeDetails:fields.deductFromFinancing', {
                                            returnObjects: true,
                                        })}
                                        {...yesNoSwitch}
                                        defaultChecked={initialValues.promoType?.discountPromoType?.deductFromFinancing}
                                        name="promoType.discountPromoType.deductFromFinancing"
                                    />
                                </Col>
                            )}
                        </>
                    )}
                    <Col {...colSpan}>
                        <InputField
                            {...t('promoCodeDetails:fields.description', { returnObjects: true })}
                            name={`promoType.${
                                values.promoType.type === PromoTypeEnum.Discount ? 'discountPromoType' : 'giftPromoType'
                            }.description`}
                            required
                        />
                    </Col>
                    <Col {...colSpan}>
                        <SwitchField
                            {...t('promoCodeDetails:fields.trackUsage', {
                                returnObjects: true,
                            })}
                            {...yesNoSwitch}
                            defaultChecked={initialValues.trackUsage}
                            name="trackUsage"
                        />
                    </Col>
                    {values.trackUsage && (
                        <Col {...colSpan}>
                            <InputNumberField
                                {...t('promoCodeDetails:fields.initialValue', { returnObjects: true })}
                                name="initialQuantity"
                                required
                            />
                        </Col>
                    )}
                    <Col {...colSpan}>
                        <SwitchField
                            {...t('promoCodeDetails:fields.active', {
                                returnObjects: true,
                            })}
                            {...yesNoSwitch}
                            defaultChecked={initialValues.isActive}
                            name="isActive"
                        />
                    </Col>
                </Row>
            </Card>
            {hasApplicationModules &&
                values?.variantModuleIds?.length > 0 &&
                values?.variantModuleIds?.map((moduleId, index) => (
                    <VehicleAssignmentsSection
                        filter={{ moduleId }}
                        header={t('promoCodeDetails:tabs.panel.vehicleAssignment', {
                            moduleName: vehicleModulesMapping[moduleId]?.displayName,
                        })}
                        name={`moduleVariantsMapping.${[moduleId]}`}
                    />
                ))}
        </Space>
    );
};

export default CreatePromoCodeFrom;
